import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

import { ROUTES } from '@common/constant/routeConstant';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';

import PurchaseRemark from '../header/purchaseSection/purchaseRemark';

import Container from '@components/store/container';
import CourseAcademicFooterBannerHighlightImage from '@components/templates/courseDetail/courseAcademicBanner/footer/highlightImage';
import { CourseAcademicFooterBannerContainer } from '@components/templates/courseDetail/courseAcademicBanner/footer/style';
import { GradientBackground } from '@components/templates/courseDetail/courseAcademicBanner/style';
import useCourseAcademicBanner from '@hooks/course/courseDetail/useCourseAcademicBanner';
import useCourseDetail from '@hooks/course/useCourseDetail';
import { AcademicCourseEnrollmentStatus, CourseEntity } from '@redux/course/type/courseDetailType';

const HEADER_HIGH_OFFSET = 0;

type CourseAcademicFooterBannerProps = {
  headerHeight: number;
  courseDetailSeo: CourseEntity;
};

const CourseAcademicFooterBanner = ({ headerHeight, courseDetailSeo }: CourseAcademicFooterBannerProps) => {
  const { t } = useTranslation('store_course');
  const router = useRouter();
  const { idName, name, university } = courseDetailSeo;
  const { examExpiredAt, enrollmentStatus, courseDetailData } = useCourseDetail();
  const { bannerPrimaryColor, bannerSecondaryColor } = useCourseAcademicBanner({
    courseDetailData,
    courseName: name,
    enrollmentStatus: enrollmentStatus as AcademicCourseEnrollmentStatus,
  });
  const [scrollY, setScrollY] = useState(0);
  const theme = useTheme();

  const handleClickContinueLearning = () => {
    router.push(ROUTES.COURSE.LEARNING(idName));
  };

  const handleScrollToTop = () => {
    import('react-scroll').then((scroll) => {
      scroll.animateScroll.scrollToTop({});
    });
  };

  const isDisableFooterRemark = [
    AcademicCourseEnrollmentStatus.ACADEMIC_AUDIT_ENROLLED,
    AcademicCourseEnrollmentStatus.ACADEMIC_AUDIT_ENROLLMENT_EXPIRED,
    AcademicCourseEnrollmentStatus.AVAILABLE_REGISTER,
  ].includes(enrollmentStatus as AcademicCourseEnrollmentStatus);

  const isContinueLeaning = [
    AcademicCourseEnrollmentStatus.ACADEMIC_CREDIT_ENROLLED,
    AcademicCourseEnrollmentStatus.ACADEMIC_CREDIT_PASSED,
    AcademicCourseEnrollmentStatus.ACADEMIC_CREDIT_PENDING,
  ].includes(enrollmentStatus as AcademicCourseEnrollmentStatus);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <CourseAcademicFooterBannerContainer isSticky={scrollY - HEADER_HIGH_OFFSET > headerHeight}>
      <GradientBackground primaryColor={bannerPrimaryColor} secondaryColor={bannerSecondaryColor}>
        <Container
          containerSx={{
            paddingBlock: {
              md: '30px',
              xs: '16px',
            },
            paddingInline: {
              lg: '0px',
              md: '24px',
            },
            height: '100%',
          }}
          rootSx={{
            height: '100%',
          }}
        >
          <Box display="flex" justifyContent="space-between" height="100%">
            <Box sx={{ display: { md: 'flex', xs: 'none', width: '70%' } }}>
              <Box
                sx={{
                  marginRight: '40px',
                  height: '100px',
                  display: {
                    lg: 'block',
                    md: 'none',
                  },
                }}
              >
                <CourseAcademicFooterBannerHighlightImage courseDetailSeo={courseDetailSeo} />
              </Box>
              <Box sx={{ paddingRight: '24px' }}>
                <Typography variant="h5" color={theme.palette.textOnPrimary.secondary}>
                  {university?.name}
                </Typography>
                <Typography
                  variant="h4"
                  color={theme.palette.textOnPrimary.primary}
                  sx={{
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    WebkitLineClamp: 3,
                  }}
                >
                  {name}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: {
                  md: '310px',
                  xs: '100%',
                },
                height: '100%',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: {
                    md: 'column',
                    xs: 'row',
                  },
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingInline: {
                    md: '0px',
                    xs: '24px',
                  },
                  gap: '16px',
                }}
              >
                {!isDisableFooterRemark && (
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <PurchaseRemark
                      sx={{ width: '100%' }}
                      enrollmentStatus={enrollmentStatus as AcademicCourseEnrollmentStatus}
                      examExpiredAt={examExpiredAt}
                      isFooter
                    />
                  </Box>
                )}
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={isContinueLeaning ? handleClickContinueLearning : handleScrollToTop}
                    fullWidth
                  >
                    {isContinueLeaning
                      ? t('course_detail.header_banner.purchase.enrolled.button.text')
                      : t('course_detail.header_banner.choose_academic_type')}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </GradientBackground>
    </CourseAcademicFooterBannerContainer>
  );
};

export default CourseAcademicFooterBanner;
